import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import Tooltip from '../components/Tooltip'
import { TRACK_RECORD_GLOSSARY as GLOSSARY } from '../config/glossary'
import { discoverAPI, type DiscoverCreator, type DiscoverSort } from '../services/api'
import { parseApiDate } from '../utils/signalExpiry'
import { TOO_FEW_TO_JUDGE } from './CreatorRecord'

const SORTS: { id: DiscoverSort; label: string; explain: string }[] = [
  {
    id: 'calls',
    label: 'Most closed calls',
    explain: 'The longest records first: more closed calls say more about a creator than a high rate from a few.',
  },
  { id: 'recent', label: 'Newest call', explain: 'Whoever locked a call most recently first.' },
  {
    id: 'win_rate_low',
    label: 'Win rate (low end)',
    explain:
      'Only creators with enough closed calls to judge, ranked by the low end of their win rate’s 95% range, so a lucky few calls can’t top it. The rest follow.',
  },
]

const pct = (value: number) => `${value.toFixed(1)}%`

const rLine = (r: number) => `${r >= 0 ? '+' : ''}${r.toFixed(2)}R`

const month = (iso: string) => format(parseApiDate(iso), 'MMM yyyy')

const CreatorRow = ({ creator: c }: { creator: DiscoverCreator }) => (
  <li className="rounded-lg border border-gray-200 bg-white p-4" data-testid="creator-card">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-semibold text-gray-900">
          {c.display_name} <span className="font-normal text-gray-500">@{c.handle}</span>
        </p>
        {c.bio && <p className="mt-0.5 line-clamp-2 text-sm text-gray-600">{c.bio}</p>}
        <p className="mt-1 text-xs text-gray-500">
          {c.closed_calls} closed · {c.open_calls} open · calling since {month(c.first_call_at)} · last call{' '}
          {format(parseApiDate(c.last_call_at), 'MMM d, yyyy')}
          {c.subscriber_count > 0 && ` · ${c.subscriber_count} subscriber${c.subscriber_count === 1 ? '' : 's'}`}
          {c.min_tier_price_cents !== null && ` · from $${(c.min_tier_price_cents / 100).toFixed(2)}/month`}
        </p>
      </div>
      <div className="flex shrink-0 gap-2">
        <Link
          to={c.record_path}
          className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
        >
          Record
        </Link>
        <Link
          to={c.profile_path}
          className="rounded-md bg-primary-600 px-3 py-1 text-sm font-medium text-white hover:bg-primary-700"
        >
          Profile
        </Link>
      </div>
    </div>
    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm" data-testid="creator-figures">
      {c.win_rate === null ? (
        <span className="text-gray-500">No closed calls yet</span>
      ) : (
        <>
          <span className="text-gray-900">
            Win rate {pct(c.win_rate)}
            {c.win_rate_low !== null && c.win_rate_high !== null && (
              <span className="text-gray-500">
                {' '}
                (95% range {c.win_rate_low.toFixed(0)}–{c.win_rate_high.toFixed(0)}%)
              </span>
            )}
          </span>
          {c.avg_r_gross !== null && (
            <span className="text-gray-900">
              {rLine(c.avg_r_gross)} <span className="text-gray-500">per call</span>
            </span>
          )}
        </>
      )}
    </div>
    {c.too_few_to_judge && (
      <p className="mt-1 text-xs font-medium text-amber-600" data-testid="too-few-to-judge">
        {TOO_FEW_TO_JUDGE} ({c.closed_calls} of {c.min_closed_to_judge})
      </p>
    )}
  </li>
)

/**
 * Find creators by their record. Every figure is the creator's public
 * record (the /c/<handle> page), before fees; nothing is ranked by a bare
 * win rate.
 */
const Creators = () => {
  const [sort, setSort] = useState<DiscoverSort>('calls')
  const [judgedOnly, setJudgedOnly] = useState(false)
  const [creators, setCreators] = useState<DiscoverCreator[] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    setFailed(false)
    discoverAPI
      .creators({ sort_by: sort, ...(judgedOnly ? { judged_only: true } : {}) })
      .then((body) => live && setCreators(body.creators))
      .catch(() => {
        if (!live) return
        setCreators([])
        setFailed(true)
      })
    return () => {
      live = false
    }
  }, [sort, judgedOnly])

  const chosen = SORTS.find((s) => s.id === sort) ?? SORTS[0]

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-8 sm:px-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Creators</h1>
        <p className="mt-2 text-gray-600">
          Every creator who has locked a call, with their record as it stands:{' '}
          <Tooltip content={GLOSSARY.grossRecord}>before fees</Tooltip>, wins and losses alike. Under{' '}
          {creators?.[0]?.min_closed_to_judge ?? 20} closed calls there are too few to judge.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <label className="block text-sm text-gray-700">
          Order
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as DiscoverSort)}
            className="mt-1 block rounded-md border border-gray-300 px-2 py-1.5 text-sm"
          >
            {SORTS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 pb-1.5 text-sm text-gray-700">
          <input type="checkbox" checked={judgedOnly} onChange={(e) => setJudgedOnly(e.target.checked)} />
          Only creators with enough calls to judge
        </label>
      </div>
      <p className="text-xs text-gray-500" data-testid="sort-explanation">
        {chosen.explain}
      </p>

      {creators === null ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : failed ? (
        <p role="alert" className="text-sm text-danger-600">
          Couldn&apos;t load creators. Try again in a minute.
        </p>
      ) : creators.length === 0 ? (
        <p className="text-sm text-gray-500">
          {judgedOnly ? 'No creator has enough closed calls to judge yet.' : 'No creator has locked a call yet.'}
        </p>
      ) : (
        <ul className="space-y-3">
          {creators.map((c) => (
            <CreatorRow key={c.creator_id} creator={c} />
          ))}
        </ul>
      )}
    </div>
  )
}

export default Creators
